/**
 * Subscriptions Page
 *
 * Page for managing organization subscription.
 * Part of the "Your Practice" feature.
 * Replicates the subscription section from UserView.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBundlingEnabled } from '@features/subscriptions';
import { useCreateCustomerPortalSessionMutation, useUserEmail } from '@features/user';
import {
  AttachMoney,
  CalendarMonth,
  CheckCircle,
  CreditCard,
  People,
  Schedule,
} from '@mui/icons-material';
import { Alert, Box, Button, Card, Chip, CircularProgress, Grid, Typography } from '@mui/material';
import { enhancedApi as generatedUsersApi } from '@store/generated/users';

// Format date/time from unix timestamp
const formatDateTime = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const userEmail = useUserEmail();
  const [createCustomerPortalSession] = useCreateCustomerPortalSessionMutation();
  const [loadingPortal, setLoadingPortal] = useState(false);
  const { isBundlingEnabled: hasBundlingFeature } = useBundlingEnabled();

  // Fetch subscription details
  const {
    data: subscriptionDetails,
    isLoading: isLoadingSubscription,
    error: subscriptionError,
  } = generatedUsersApi.useGetV2ProviderSubscriptionDetailsQuery(undefined);

  const getStatusColor = (
    status: string | undefined | null,
  ): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    if (!status) return 'default';
    const statusLower = status.toLowerCase();
    if (statusLower === 'active') return 'success';
    if (statusLower === 'trialing') return 'info';
    if (statusLower === 'canceled' || statusLower === 'cancelled') return 'error';
    if (statusLower === 'past_due') return 'warning';
    return 'default';
  };

  // Format currency amount
  const formatCurrency = (amount: number | null | undefined, currency: string = 'usd'): string => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Stripe amounts are in cents
  };

  // Get plan details from items
  const planDetails = subscriptionDetails?.items?.[0]?.price;
  const planAmount = planDetails?.unit_amount;
  const planCurrency = planDetails?.currency || 'usd';
  const planInterval = planDetails?.recurring?.interval || 'month';

  // Safe color palette accessor
  const getColorPalette = (status: string | undefined | null) => {
    const statusColor = getStatusColor(status);
    const validColors = ['success', 'info', 'error', 'warning'] as const;
    return validColors.includes(statusColor as (typeof validColors)[number])
      ? (statusColor as 'success' | 'info' | 'error' | 'warning')
      : 'primary';
  };

  const handleManageSubscription = useCallback(async () => {
    // Feature bundling users should go to internal subscriptions page
    if (hasBundlingFeature) {
      navigate('/practice/subscriptions');
      return;
    }

    setLoadingPortal(true);

    try {
      const data = await createCustomerPortalSession({}).unwrap();

      if (data?.portal_session_url) {
        window.open(data.portal_session_url, '_blank');
      } else {
        // Fallback to hardcoded Stripe link
        window.open(
          `https://billing.stripe.com/p/login/7sIdRo5NjdyN1QA8ww?prefilled_email=${encodeURIComponent(
            userEmail || '',
          )}`,
          '_blank',
        );
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      // Fallback to hardcoded Stripe link
      window.open(
        `https://billing.stripe.com/p/login/7sIdRo5NjdyN1QA8ww?prefilled_email=${encodeURIComponent(
          userEmail || '',
        )}`,
        '_blank',
      );
    } finally {
      setLoadingPortal(false);
    }
  }, [createCustomerPortalSession, userEmail, hasBundlingFeature, navigate]);

  return (
    <>
      {/* Subscription Details */}
      {isLoadingSubscription ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : subscriptionError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Unable to load subscription details. Please try again later.
        </Alert>
      ) : subscriptionDetails && subscriptionDetails.has_subscription ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Subscription Status Card */}
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Card
              elevation={2}
              sx={{
                height: '100%',
                background: theme =>
                  subscriptionDetails.status
                    ? `linear-gradient(135deg, ${theme.palette[getColorPalette(subscriptionDetails.status)]?.main || theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 100%)`
                    : theme.palette.background.paper,
                border: theme =>
                  `1px solid ${subscriptionDetails.status ? theme.palette[getColorPalette(subscriptionDetails.status)]?.main || theme.palette.primary.main : theme.palette.divider}`,
              }}
            >
              <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CheckCircle
                    sx={{
                      fontSize: 28,
                      color: theme =>
                        theme.palette[getColorPalette(subscriptionDetails.status)]?.main ||
                        theme.palette.primary.main,
                      mr: 1.5,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                    >
                      Status
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.25 }}>
                      {subscriptionDetails.status
                        ? subscriptionDetails.status.charAt(0).toUpperCase() +
                          subscriptionDetails.status.slice(1)
                        : 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={subscriptionDetails.status || 'Unknown'}
                  color={getStatusColor(subscriptionDetails.status)}
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              </Box>
            </Card>
          </Grid>

          {/* Plan & Pricing Card */}
          {planAmount && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  background: theme =>
                    `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 100%)`,
                  border: theme => `1px solid ${theme.palette.primary.main}`,
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <AttachMoney
                      sx={{
                        fontSize: 28,
                        color: 'primary.main',
                        mr: 1.5,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                      >
                        Plan
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.25 }}>
                        {formatCurrency(planAmount, planCurrency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                        per {planInterval}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Billing Period Card */}
          {subscriptionDetails.current_period_start && subscriptionDetails.current_period_end && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Schedule sx={{ fontSize: 24, color: 'primary.main', mr: 1.5 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                    >
                      Current Period
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {formatDateTime(subscriptionDetails.current_period_start)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    to
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatDateTime(subscriptionDetails.current_period_end)}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Trial Period Card */}
          {subscriptionDetails.trial_end && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  background: theme =>
                    `linear-gradient(135deg, ${theme.palette.info.main}08 0%, ${theme.palette.background.paper} 100%)`,
                  border: theme => `1px solid ${theme.palette.info.light}`,
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <CalendarMonth sx={{ fontSize: 24, color: 'info.main', mr: 1.5 }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                    >
                      Trial Period
                    </Typography>
                  </Box>
                  {subscriptionDetails.trial_start && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.5, display: 'block' }}
                    >
                      Started: {formatDateTime(subscriptionDetails.trial_start)}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Ends: {formatDateTime(subscriptionDetails.trial_end)}
                  </Typography>
                  <Chip
                    label={(() => {
                      const trialEndMs = subscriptionDetails.trial_end * 1000;
                      const daysLeft = Math.ceil((trialEndMs - Date.now()) / (1000 * 60 * 60 * 24));
                      return daysLeft > 0 ? `${daysLeft} days remaining` : 'Trial ended';
                    })()}
                    color="info"
                    size="small"
                    sx={{ mt: 0.5, fontWeight: 500 }}
                  />
                </Box>
              </Card>
            </Grid>
          )}

          {/* Quantity/Seats Card - Only show if more than 1 seat */}
          {subscriptionDetails.quantity && subscriptionDetails.quantity > 1 && (
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Card elevation={2} sx={{ height: '100%' }}>
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <People sx={{ fontSize: 28, color: 'primary.main', mb: 0.5 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', fontWeight: 600 }}
                  >
                    Seats
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {subscriptionDetails.quantity}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          )}

          {/* Cancel at Period End Warning */}
          {subscriptionDetails.cancel_at_period_end && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" sx={{ borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Your subscription will cancel at the end of the current period.
                </Typography>
              </Alert>
            </Grid>
          )}
        </Grid>
      ) : subscriptionDetails && !subscriptionDetails.has_subscription ? (
        <Card elevation={2} sx={{ p: 3, textAlign: 'center', mb: 2 }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No Active Subscription
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don&apos;t have an active subscription at this time.
          </Typography>
        </Card>
      ) : null}

      {/* Manage Subscription Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 3 }}>
        <Button
          variant="contained"
          onClick={handleManageSubscription}
          disabled={loadingPortal}
          startIcon={loadingPortal ? <CircularProgress size={16} /> : <CreditCard />}
          sx={{
            px: 3,
            py: 1,
            fontWeight: 600,
          }}
        >
          {loadingPortal ? 'Loading...' : 'Manage Subscription'}
        </Button>
      </Box>
    </>
  );
}
